import { ConvexHull } from "three/examples/jsm/math/ConvexHull";
import { v3 } from "oc-threejs/short3";
import { Geometry } from "oc-threejs/geometries/Geometry";

import {
  Box3,
  BoxGeometry,
  BufferGeometry,
  Float32BufferAttribute,
  Matrix4,
  SphereGeometry,
  Vector3,
} from "three";

/**
 * Kopiert von ConvexBufferGeometry und erweitert um uv-Mapping
 */
export class ConvexUvBufferGeometry extends BufferGeometry {
  basis: Matrix4;

  /**
   * @param inputVertices die Punktwolke
   * @param basis für die UV Berechnung verwendete Basis
   */
  constructor(inputVertices: Vector3[], basis: Matrix4 = new Matrix4()) {
    super();
    this.basis = basis;

    // buffers
    const vertices: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];

    const xAxis = v3();
    const yAxis = v3();
    const zAxis = v3();
    basis.extractBasis(xAxis, yAxis, zAxis);
    const inverseBrettBasis = new Matrix4().copy(basis).invert();
    const uvBoundingBox = new Box3().setFromPoints(
      inputVertices.map((v) => v.clone().applyMatrix4(inverseBrettBasis)),
    );

    // UV-Verlauf der Texturen in der Ebene zur jeweiligen Brett-Basis-Normalen
    const planeBasisUV_X = ["y", "z"];
    const planeBasisUV_Y = ["z", "x"];
    const planeBasisUV_Z = ["y", "x"];

    new ConvexHull().setFromPoints(inputVertices).faces.forEach((face: any) => {
      let edge = face.edge;

      // Ermittlung der planeBasisUV anhand der am besten übereinstimmenden Basis-Achse
      const xAxisDot = Math.abs(face.normal.dot(xAxis));
      const yAxisDot = Math.abs(face.normal.dot(yAxis));
      const zAxisDot = Math.abs(face.normal.dot(zAxis));
      let planeBasisUV: string[];
      if (yAxisDot < xAxisDot) {
        if (zAxisDot < xAxisDot) {
          planeBasisUV = planeBasisUV_X;
        } else {
          planeBasisUV = planeBasisUV_Z;
        }
      } else {
        if (zAxisDot < yAxisDot) {
          planeBasisUV = planeBasisUV_Y;
        } else {
          planeBasisUV = planeBasisUV_Z;
        }
      }

      // we move along a doubly-connected edge list to access all face points (see HalfEdge
      // docs)
      do {
        const point = edge.head().point;
        vertices.push(point.x, point.y, point.z);
        normals.push(face.normal.x, face.normal.y, face.normal.z);

        const pointUV = point.clone().applyMatrix4(inverseBrettBasis);
        const uv = pointUV.sub(uvBoundingBox.min);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        uvs.push(uv[planeBasisUV[0]], uv[planeBasisUV[1]]);

        edge = edge.next;
      } while (edge !== face.edge);
    });

    // build geometry
    this.setAttribute("position", new Float32BufferAttribute(vertices, 3));
    this.setAttribute("normal", new Float32BufferAttribute(normals, 3));
    this.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  }

  /**
   * Convenience-Methode, um eine Box zu erzeugen. Alle Parameter tragen default-Werte und können
   * weg gelassen werden. Im Gegensatz zu einer THREE.BoxGeometry sind die UV-Koordinaten
   * entsprechend der Size angepasst.
   */
  static createBox(
    width?: number,
    height?: number,
    depth?: number,
    widthSegments?: number,
    heightSegments?: number,
    depthSegments?: number,
  ) {
    return new ConvexUvBufferGeometry(
      new Geometry().fromBufferGeometry(
        new BoxGeometry(width, height, depth, widthSegments, heightSegments, depthSegments),
      ).vertices,
    );
  }

  /**
   * Convenience-Methode, um eine Sphere zu erzeugen. Alle Parameter tragen default-Werte und
   * können weg gelassen werden. Im Gegensatz zu einer THREE.SphereGeometry sind die
   * UV-Koordinaten entsprechend der Size angepasst.
   */
  static createSphere(
    radius?: number,
    widthSegments?: number,
    heightSegments?: number,
    phiStart?: number,
    phiLength?: number,
    thetaStart?: number,
    thetaLength?: number,
  ) {
    return new ConvexUvBufferGeometry(
      new Geometry().fromBufferGeometry(
        new SphereGeometry(
          radius,
          widthSegments,
          heightSegments,
          phiStart,
          phiLength,
          thetaStart,
          thetaLength,
        ),
      ).vertices,
    );
  }

  /**
   * Rundet eine beliebige Geometry ab, d.h. alle Ecken und Kanten werden abgerundet, und gibt
   * eine neue Geometry zurück.
   *
   * @param geometry
   * @param bevelDistance Breite der Abrundung
   * @param bevelSteps wie oft wird intern gebevelt, größere Werte ergeben rundere Kanten, bei 1
   *     einfach unterteilt = eine 45° abgeschrägte Kante, bei 2 wird diese wiederum in zwei
   *     Teilkanten unterteilt usw.
   * @param basis für die UV-Berechnung verwendete Koordinatenbasis
   * @return ConvexUvBufferGeometry
   */
  static bevelGeometry(
    geometry: Geometry | BufferGeometry,
    bevelDistance: number = 0.01,
    bevelSteps: number = 2,
    basis: Matrix4 = new Matrix4(),
  ) {
    const bevel = (geo: Geometry, bevelFactor: number) => {
      const vertices: Vector3[] = [];
      const _0 = v3();
      const _1 = v3();
      const addBeveledPoint = (
        a: Vector3,
        b: Vector3,
        c: Vector3,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        n: Vector3,
      ) => {
        _0.subVectors(b, a).normalize().multiplyScalar(bevelFactor);
        _1.subVectors(c, a).normalize().multiplyScalar(bevelFactor);
        vertices.push(a.clone().add(_0).add(_1));
      };
      geo.faces.forEach((f) => {
        const a = geo.vertices[f.a];
        const b = geo.vertices[f.b];
        const c = geo.vertices[f.c];
        addBeveledPoint(a, b, c, f.normal);
        addBeveledPoint(b, c, a, f.normal);
        addBeveledPoint(c, a, b, f.normal);
      });
      return new ConvexUvBufferGeometry(vertices, basis);
    };

    let currentGeometry: Geometry | BufferGeometry = geometry;

    for (let i = 0; i < bevelSteps; i++) {
      let geoForStep: Geometry;
      if (currentGeometry instanceof BufferGeometry) {
        geoForStep = new Geometry().fromBufferGeometry(currentGeometry);
      } else {
        geoForStep = currentGeometry;
      }
      currentGeometry = bevel(geoForStep, bevelDistance / bevelSteps);
    }

    return currentGeometry;
  }
}
